import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapContainer } from "react-leaflet";
import RouterForwarder from "../RouterForwarder.js";
import MapController from "./MapController.js";
import MapLayers from "./MapLayers.js";
import ProjectMarkers from "./ProjectMarkers.js";
import { useMapState } from "../hooks/useMapState.js";
import { MAP_CONSTANTS } from "../constants/mapConstants.js";
import TenementsLegend from "./TenementsLegend.js";
import { applyQueuedBounds } from "../utils/mapBounds.js";

const MapView = ({ geojson, markersVisible, tenementsLayerVisible, nextBounds, projects, mapLayers, sidebarItems, clearNextBounds }) => {
  const { state, dispatch, mapRef, handleMovement, getTenementInfo, filterMarkersByCompany, updateAllMarkers, onMapCreated } = useMapState(
    projects,
    tenementsLayerVisible,
    clearNextBounds,
    mapLayers,
    sidebarItems
  );
  const [projectsReady, setProjectsReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Effect for URL changes and project data changes
  useEffect(() => {
    if (!projects) return;

    const currentPath = location.pathname;
    const companyMatch = currentPath.match(/^\/c\/([^/]+)$/) || currentPath.match(/^\/g\/([^/]+)$/);

    if (companyMatch && companyMatch[1]) {
      const companyIdentifier = companyMatch[1];

      if (companyIdentifier !== state.activeCompany) {
        filterMarkersByCompany(companyIdentifier);
      }
    } else if (state.filterActive) {
      updateAllMarkers();
    } else if (state.markers.length === 0) {
      updateAllMarkers();
    }
  }, [
    projects,
    location.pathname,
    state.filterActive,
    state.activeCompany,
    state.markers.length,
    filterMarkersByCompany,
    updateAllMarkers,
  ]);

  // Effect to track when projects are ready
  useEffect(() => {
    if (projects && Object.keys(projects).length > 0) {
      setProjectsReady(true);
    }
  }, [projects]);

  // Effect to update markers only when both projects and map are ready
  useEffect(() => {
    if (projectsReady && state.mapLoaded && state.markers.length === 0) {
      updateAllMarkers();
    }
  }, [projectsReady, state.mapLoaded, state.markers.length, updateAllMarkers]);


  useEffect(() => {
    applyQueuedBounds(mapRef.current, nextBounds, state.mapLoaded, clearNextBounds);
  }, [nextBounds, state.mapLoaded, clearNextBounds, mapRef]);

  useEffect(() => {
    if (state.mapLoaded && projects) {
      updateAllMarkers();
    }
  }, [projects, state.mapLoaded, updateAllMarkers]);

  return (
    <RouterForwarder context={{ history: null, location, match: null }}>
      {state.mapDelay ? (
        <div>Loading...</div>
      ) : (
        <>
          <MapContainer
            key="map-container"
            center={MAP_CONSTANTS.mapCenter}
            zoom={MAP_CONSTANTS.zoomLevel}
            minZoom={2.5}
            maxZoom={13}
            zoomControl={false}
            maxBounds={MAP_CONSTANTS.worldBounds}
            maxBoundsViscosity={1.0}
            zoomSnap={0.7}
            zoomDelta={0.1}
            attributionControl={false}
            preferCanvas={false}
            onClick={getTenementInfo}>
            <MapController onMapCreated={onMapCreated} />

            <MapLayers
              layers={state.layers}
              geojson={geojson}
              tenementsLayerVisible={tenementsLayerVisible}
              offlineCoastline={state.offlineCoastline}
              selectedTenement={state.selectedTenement}
            />

            {state.markers.length > 0 && state.mapLoaded && (
              <ProjectMarkers
                markers={state.markers}
                visible={markersVisible !== false}
                cluster={!state.filterActive}
                key={`markers-${state.markers.length}`}
              />
            )}

            {tenementsLayerVisible && <TenementsLegend />}
          </MapContainer>
        </>
      )}
    </RouterForwarder>
  );
};

export default MapView;
